import { UNIDADES } from "@caint/data";
import { Header, Sheet } from "@caint/design-system";
import type { AbrirCicloDTO, CicloPublico } from "@caint/shared";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { EditarCicloForm } from "../components/EditarCicloForm.tsx";
import { FormularioPublicoPainel } from "../components/FormularioPublicoPainel.tsx";
import { FILTRO_CICLOS_VAZIO, type FiltroCiclos, filtrarCiclos, opcoesFiltro } from "../lib/ciclos.ts";
import { mensagemAmigavel } from "../lib/erros.ts";
import { filtrarUnidades } from "../lib/unidades.ts";
import type { CiclosApi } from "../state/useAppState.ts";

export interface CiclosProps {
  ciclosApi: CiclosApi;
  recarregar: () => Promise<void>;
  podeAbrir: boolean;
  /** Nome do ator (gestor) — trilha do escopo ao editar. */
  autor: string;
  onBack: () => void;
  /** Avaliação do ciclo atualmente ativo (destaque + desabilita "Ativar"). */
  avaliacaoIdAtivo?: string | null;
  /** Ativa o ciclo como ciclo global (passa a avaliação ao seletor). */
  onAtivar?: (avaliacaoId: string) => void;
}

function msg(e: unknown): string {
  return mensagemAmigavel(e);
}
function fmt(iso: string): string {
  return iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";
}

const MESES_LABEL = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

/** Barra de filtros (ano/mês/unidade) — deriva as opções da lista carregada. */
function FiltrosCiclos({
  ciclos,
  filtro,
  onFiltro,
}: {
  ciclos: readonly CicloPublico[];
  filtro: FiltroCiclos;
  onFiltro: (f: FiltroCiclos) => void;
}) {
  const opc = useMemo(() => opcoesFiltro(ciclos), [ciclos]);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
      <select
        className="m-select"
        aria-label="Ano"
        value={filtro.ano ?? ""}
        onChange={(e) => onFiltro({ ...filtro, ano: e.target.value ? Number(e.target.value) : null })}
      >
        <option value="">Ano</option>
        {opc.anos.map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
      <select
        className="m-select"
        aria-label="Mês"
        value={filtro.mes ?? ""}
        onChange={(e) => onFiltro({ ...filtro, mes: e.target.value ? Number(e.target.value) : null })}
      >
        <option value="">Mês</option>
        {opc.meses.map((m) => (
          <option key={m} value={m}>
            {MESES_LABEL[m - 1]}
          </option>
        ))}
      </select>
      <select
        className="m-select"
        aria-label="Unidade"
        value={filtro.unidadeId ?? ""}
        onChange={(e) => onFiltro({ ...filtro, unidadeId: e.target.value || null })}
      >
        <option value="">Unidade</option>
        {opc.unidades.map((u) => (
          <option key={u.id} value={u.id}>
            {u.nome}
          </option>
        ))}
      </select>
    </div>
  );
}

export function Ciclos({
  ciclosApi,
  recarregar,
  podeAbrir,
  autor,
  onBack,
  avaliacaoIdAtivo,
  onAtivar,
}: CiclosProps) {
  const [ciclos, setCiclos] = useState<CicloPublico[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [abrindo, setAbrindo] = useState(false);
  const [painel, setPainel] = useState<CicloPublico | null>(null);
  const [editando, setEditando] = useState<CicloPublico | null>(null);
  const [filtro, setFiltro] = useState<FiltroCiclos>(FILTRO_CICLOS_VAZIO);
  const visiveis = useMemo(() => (ciclos ? filtrarCiclos(ciclos, filtro) : []), [ciclos, filtro]);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      setCiclos(await ciclosApi.listar());
    } catch (e) {
      setErro(msg(e));
      setCiclos([]);
    }
  }, [ciclosApi]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const encerrar = async (id: string) => {
    setErro(null);
    try {
      await ciclosApi.encerrar(id);
      await carregar();
      await recarregar();
    } catch (e) {
      setErro(msg(e));
    }
  };

  return (
    <Fragment>
      <Header title="Ciclos de Avaliação" sub="Abertura por unidade e período" onBack={onBack} />
      <div className="m-body">
        {podeAbrir && (
          <button
            type="button"
            className="m-btn primary"
            style={{ marginBottom: 14 }}
            onClick={() => setAbrindo(true)}
          >
            + Abrir ciclo
          </button>
        )}
        {erro && (
          <p
            style={{
              color: "var(--danger)",
              background: "var(--danger-soft)",
              borderRadius: 10,
              padding: "8px 12px",
              marginBottom: 12,
            }}
          >
            {erro}
          </p>
        )}
        {ciclos && ciclos.length > 0 && (
          <FiltrosCiclos ciclos={ciclos} filtro={filtro} onFiltro={setFiltro} />
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {ciclos === null && <p className="spec">Carregando…</p>}
          {ciclos?.length === 0 && <p className="spec">Nenhum ciclo ainda.</p>}
          {ciclos && ciclos.length > 0 && visiveis.length === 0 && (
            <p className="spec">Nenhum ciclo para os filtros escolhidos.</p>
          )}
          {visiveis.map((c) => {
            const ativo = !!c.avaliacaoId && c.avaliacaoId === avaliacaoIdAtivo;
            return (
              <div
                key={c.id}
                className="m-card pad"
                style={
                  ativo
                    ? { border: "1px solid var(--primary)", background: "var(--primary-soft)" }
                    : undefined
                }
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <b style={{ flex: 1 }}>{c.unidadeNome}</b>
                  {ativo && (
                    <span className="m-pill" style={{ background: "var(--primary)", color: "#fff" }}>
                      ativo
                    </span>
                  )}
                  <span
                    className="m-pill"
                    style={{
                      background: c.status === "aberto" ? "var(--mat-3-soft)" : "var(--surface-2)",
                      color: c.status === "aberto" ? "var(--mat-4)" : "var(--text-muted)",
                    }}
                  >
                    {c.status}
                  </span>
                </div>
                <div className="spec" style={{ marginTop: 4 }}>
                  {fmt(c.dataInicio)} → {fmt(c.dataFim)}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                  {onAtivar && c.avaliacaoId && (
                    <button
                      type="button"
                      className={`m-btn${ativo ? "" : " primary"}`}
                      style={{ fontSize: 12.5 }}
                      disabled={ativo}
                      onClick={() => c.avaliacaoId && onAtivar(c.avaliacaoId)}
                    >
                      {ativo ? "Ciclo ativo" : "Ativar"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="m-btn"
                    style={{ fontSize: 12.5 }}
                    onClick={() => setPainel(c)}
                  >
                    Formulário & IMGR
                  </button>
                  {podeAbrir && (
                    <button
                      type="button"
                      className="m-btn"
                      style={{ fontSize: 12.5 }}
                      onClick={() => setEditando(c)}
                    >
                      Editar
                    </button>
                  )}
                  {podeAbrir && c.status === "aberto" && (
                    <button
                      type="button"
                      className="m-btn"
                      style={{ fontSize: 12.5 }}
                      onClick={() => void encerrar(c.id)}
                    >
                      Encerrar ciclo
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {abrindo && (
        <AbrirCicloSheet
          ciclosApi={ciclosApi}
          onClose={() => setAbrindo(false)}
          onAberto={async () => {
            setAbrindo(false);
            await carregar();
            await recarregar();
          }}
        />
      )}
      {painel && (
        <Sheet title="Formulário público" sub={painel.unidadeNome} onClose={() => setPainel(null)}>
          <FormularioPublicoPainel ciclo={painel} carregarResultado={ciclosApi.resultado} />
        </Sheet>
      )}
      {editando && (
        <Sheet title="Editar ciclo" sub={editando.unidadeNome} onClose={() => setEditando(null)}>
          <EditarCicloForm
            ciclo={editando}
            ciclosApi={ciclosApi}
            autor={autor}
            onSaved={async () => {
              setEditando(null);
              await carregar();
              await recarregar();
            }}
          />
        </Sheet>
      )}
    </Fragment>
  );
}

function maisTrintaDias(base: string): string {
  const d = new Date(`${base}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + 30);
  return d.toISOString().slice(0, 10);
}
function hojeISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function AbrirCicloSheet({
  ciclosApi,
  onClose,
  onAberto,
}: { ciclosApi: CiclosApi; onClose: () => void; onAberto: () => void }) {
  const [tipo, setTipo] = useState("");
  const [busca, setBusca] = useState("");
  const tipos = useMemo(() => [...new Set(UNIDADES.map((u) => u.tipo))].sort(), []);
  const filtradas = useMemo(() => filtrarUnidades(tipo, busca), [tipo, busca]);
  const [unidadeId, setUnidadeId] = useState(UNIDADES[0]?.id ?? "");
  const [inicio, setInicio] = useState(hojeISO());
  const [fim, setFim] = useState(maisTrintaDias(hojeISO()));
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Mantém a seleção válida quando o filtro muda.
  useEffect(() => {
    if (!filtradas.some((u) => u.id === unidadeId)) setUnidadeId(filtradas[0]?.id ?? "");
  }, [filtradas, unidadeId]);

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    const dto: AbrirCicloDTO = {
      unidadeId,
      dataInicio: new Date(`${inicio}T00:00:00.000Z`).toISOString(),
      dataFim: new Date(`${fim}T23:59:59.000Z`).toISOString(),
    };
    try {
      await ciclosApi.abrir(dto);
      onAberto();
    } catch (e) {
      setErro(msg(e));
      setSalvando(false);
    }
  };

  return (
    <Sheet
      title="Abrir ciclo"
      sub="Escolha a unidade e o período"
      onClose={onClose}
      foot={
        <button
          type="button"
          className="m-btn primary"
          disabled={salvando || !unidadeId}
          onClick={() => void salvar()}
        >
          {salvando ? "Abrindo…" : "Abrir ciclo"}
        </button>
      }
    >
      {erro && <p style={{ color: "var(--danger)", marginBottom: 10 }}>{erro}</p>}
      <div className="m-field">
        <label htmlFor="ci-tipo">Tipo</label>
        <select id="ci-tipo" className="m-select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
          <option value="">Todos os tipos</option>
          {tipos.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <div className="m-field">
        <label htmlFor="ci-busca">Buscar</label>
        <input
          id="ci-busca"
          className="m-input"
          placeholder="Nome ou município"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </div>
      <div className="m-field">
        <label htmlFor="ci-un">Unidade ({filtradas.length})</label>
        <select
          id="ci-un"
          className="m-select"
          value={unidadeId}
          onChange={(e) => setUnidadeId(e.target.value)}
        >
          {filtradas.map((u) => (
            <option key={u.id} value={u.id}>
              {u.nome} · {u.municipio}
            </option>
          ))}
        </select>
      </div>
      <div className="m-field">
        <label htmlFor="ci-ini">Início</label>
        <input
          id="ci-ini"
          type="date"
          className="m-input"
          value={inicio}
          onChange={(e) => setInicio(e.target.value)}
        />
      </div>
      <div className="m-field">
        <label htmlFor="ci-fim">Fim</label>
        <input
          id="ci-fim"
          type="date"
          className="m-input"
          value={fim}
          onChange={(e) => setFim(e.target.value)}
        />
      </div>
    </Sheet>
  );
}
